import { hasValue } from '@repo/utils';

import type { FactTrajectoryPointEntity, TPointData } from '../../../service/entities';
import type { TGraphTrajectoryView } from '../../../types/graph-trajectory';
import { MAIN_TRAJECTORY_LINE_WIDTH, REFERENCE_TRAJECTORY_LINE_WIDTH } from '../../../utils/trajectory-line-colors';

import type { Trajectory2dAxisField, Trajectory2dChartConfig } from './trajectory-2d-chart-configs';

/** Single-series chart point: [x, y] */
export type Trajectory2dChartPoint = [x: number, y: number];

export type Trajectory2dSeriesData = {
  trajectoryId: number;
  trajectoryTitle: string;
  field: keyof TPointData;
  labelKey: string;
  axisIndex: number;
  color: string;
  lineWidth: number;
  data: Trajectory2dChartPoint[];
};

export type Trajectory2dMappedChart = {
  series: Trajectory2dSeriesData[];
  yInvert: boolean;
  yLabelKey: string;
};

export type Trajectory2dChartMappingSpec = Pick<Trajectory2dChartConfig, 'xFields' | 'yField'>;

function readField(point: FactTrajectoryPointEntity, field: keyof TPointData): number | null {
  const value = point.data[field];

  return typeof value === 'number' ? value : null;
}

function mapSeriesData(
  points: FactTrajectoryPointEntity[],
  xField: Trajectory2dAxisField,
  yField: Trajectory2dChartConfig['yField'],
): Trajectory2dChartPoint[] {
  const data = points
    .map((point) => {
      const x = readField(point, xField.field);
      const y = readField(point, yField.field);

      if (!hasValue(x) || !hasValue(y)) {
        return null;
      }

      return [x, y] satisfies Trajectory2dChartPoint;
    })
    .filter(hasValue);

  // Sort by Y when Y is a depth-like axis, otherwise keep source order.
  if (yField.invert) {
    data.sort((left, right) => left[1] - right[1]);
  }

  return data;
}

export function mapTrajectoryPointsByConfig(
  trajectories: TGraphTrajectoryView[],
  { xFields, yField }: Trajectory2dChartMappingSpec,
): Trajectory2dMappedChart {
  const series = trajectories.flatMap((trajectory) =>
    xFields.map((xField) => ({
      trajectoryId: trajectory.id,
      trajectoryTitle: trajectory.title,
      field: xField.field,
      labelKey: xField.labelKey,
      axisIndex: xField.axisIndex ?? 0,
      color: trajectory.color,
      lineWidth: trajectory.role === 'main' ? MAIN_TRAJECTORY_LINE_WIDTH : REFERENCE_TRAJECTORY_LINE_WIDTH,
      data: mapSeriesData(trajectory.entity.points, xField, yField),
    })),
  );

  return {
    series,
    yInvert: Boolean(yField.invert),
    yLabelKey: yField.labelKey,
  };
}
